/**
 * Parse a YYYY-MM-DD date string as a local date (NOT as UTC midnight).
 * 
 * JavaScript's `new Date("2026-06-22")` interprets date-only strings as UTC midnight,
 * which causes an off-by-one-day bug when displayed in timezones behind UTC (e.g. Peru UTC-5).
 * 
 * This function splits the string and creates a Date at local midnight, avoiding the timezone shift.
 * 
 * Usage:
 *   parseLocalDate("2026-06-22") // → Date at local midnight June 22
 *   parseLocalDate(null)         // → null
 *   parseLocalDate("")           // → null
 */
export function parseLocalDate(dateString) {
  if (!dateString) return null
  const parts = dateString.split('-')
  if (parts.length !== 3) return null
  const [year, month, day] = parts.map(Number)
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null
  return new Date(year, month - 1, day)
}

/**
 * Format a YYYY-MM-DD date string using toLocaleDateString with the given locale and options.
 * Uses parseLocalDate internally to avoid the UTC timezone bug.
 * 
 * Usage:
 *   formatLocalDate("2026-06-22")                    // → "22/6/2026" (es-PE default)
 *   formatLocalDate("2026-06-22", 'es-PE', { ... })  // → custom format
 */
export function formatLocalDate(dateString, locale = 'es-PE', options = {}) {
  const date = parseLocalDate(dateString)
  if (!date) return ''
  return date.toLocaleDateString(locale, options)
}
